import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  Shield,
  ChefHat,
  Eye,
  X
} from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  description: string;
  role: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Workers: React.FC = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    role: '',
    image_url: '',
    is_active: true,
  });

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .order('name');

      if (error) throw error;
      setWorkers(data || []);
    } catch (error) {
      console.error('Error loading workers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openModal = (worker?: Worker) => {
    if (worker) {
      setEditingWorker(worker);
      setFormData({
        name: worker.name,
        description: worker.description,
        role: worker.role,
        image_url: worker.image_url,
        is_active: worker.is_active,
      });
    } else {
      setEditingWorker(null);
      setFormData({
        name: '',
        description: '',
        role: '',
        image_url: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWorker(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const workerData = {
        name: formData.name,
        description: formData.description,
        role: formData.role,
        image_url: formData.image_url,
        is_active: formData.is_active,
      };

      if (editingWorker) {
        const { error } = await supabase
          .from('workers')
          .update({
            ...workerData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingWorker.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('workers')
          .insert(workerData);

        if (error) throw error;
      }

      await loadWorkers();
      closeModal();
    } catch (error) {
      console.error('Error saving worker:', error);
      alert('Error al guardar el trabajador');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteWorker = async (workerId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este trabajador?')) return;

    try {
      const { error } = await supabase
        .from('workers')
        .delete()
        .eq('id', workerId);

      if (error) throw error;

      await loadWorkers();
    } catch (error) {
      console.error('Error deleting worker:', error);
      alert('Error al eliminar el trabajador');
    }
  };

  const toggleActive = async (workerId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('workers')
        .update({
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', workerId);

      if (error) throw error;

      await loadWorkers();
    } catch (error) {
      console.error('Error toggling worker status:', error);
    }
  };

  const getRoleIcon = (role: string) => {
    const roleLower = role.toLowerCase();
    if (roleLower.includes('propietario') || roleLower.includes('dueño')) {
      return <Shield className="w-4 h-4 text-purple-600" />;
    } else if (roleLower.includes('admin') || roleLower.includes('administrador') || roleLower.includes('encargado')) {
      return <Shield className="w-4 h-4 text-blue-600" />;
    } else if (roleLower.includes('preparador') || roleLower.includes('carnes')) {
      return <ChefHat className="w-4 h-4 text-orange-600" />;
    } else if (roleLower.includes('vigilante')) {
      return <Shield className="w-4 h-4 text-green-600" />;
    } else {
      return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleColor = (role: string) => {
    const roleLower = role.toLowerCase();
    if (roleLower.includes('propietario') || roleLower.includes('dueño')) {
      return 'bg-purple-100 text-purple-800';
    } else if (roleLower.includes('admin') || roleLower.includes('administrador') || roleLower.includes('encargado')) {
      return 'bg-blue-100 text-blue-800';
    } else if (roleLower.includes('preparador') || roleLower.includes('carnes')) {
      return 'bg-orange-100 text-orange-800';
    } else if (roleLower.includes('vigilante')) {
      return 'bg-green-100 text-green-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando trabajadores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trabajadores</h1>
          <p className="mt-2 text-gray-600">Gestiona el equipo de Mundo Carne</p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Trabajador
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar trabajadores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Workers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredWorkers.map((worker) => (
          <div key={worker.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center overflow-hidden">
                    {worker.image_url ? (
                      <img
                        src={worker.photo_url}
                        alt={worker.name}
                        className="w-12 h-12 rounded-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="text-red-600 font-semibold text-sm">
                        {worker.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{worker.name}</h3>
                    <div className="flex items-center space-x-1 mt-1">
                      {getRoleIcon(worker.role)}
                      <span className={`text-xs px-2 py-1 rounded-full ${getRoleColor(worker.role)}`}>
                        {worker.role}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => toggleActive(worker.id, worker.is_active)}
                    className={`p-1.5 rounded-full ${
                      worker.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}
                    title={worker.is_active ? 'Trabajador activo' : 'Trabajador inactivo'}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{worker.description}</p>

              <div className="flex items-center justify-between">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  worker.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {worker.is_active ? 'Activo' : 'Inactivo'}
                </span>

                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(worker)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar trabajador"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteWorker(worker.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar trabajador"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredWorkers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No se encontraron trabajadores' : 'No hay trabajadores registrados'}
          </h3>
          <p className="text-gray-500">
            {searchTerm
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Comienza agregando miembros de tu equipo'
            }
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingWorker ? 'Editar Trabajador' : 'Nuevo Trabajador'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rol/Puesto *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar rol</option>
                  <option value="Propietario/Dueño">Propietario/Dueño</option>
                  <option value="Administrador">Administrador</option>
                  <option value="Encargado">Encargado</option>
                  <option value="Preparador de Carnes">Preparador de Carnes</option>
                  <option value="Vigilante">Vigilante</option>
                  <option value="Recepcionista">Recepcionista</option>
                  <option value="Repartidor">Repartidor</option>
                  <option value="Ayudante General">Ayudante General</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Breve descripción del trabajador..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL de Imagen</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si no proporcionas una imagen, se mostrará la inicial del nombre
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Trabajador activo
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Guardando...' : (editingWorker ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workers;